import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  FileText,
  Euro,
  Calendar,
  Shield,
  Download,
  ArrowLeft
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) =>
  new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR", minimumFractionDigits: 2 }).format(v || 0);

const ProposalSignPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [proposal, setProposal] = useState(null);
  const [error, setError] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [rejected, setRejected] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [signing, setSigning] = useState(false);
  const [responded, setResponded] = useState(false);

  useEffect(() => { loadProposal(); }, [token]);

  const loadProposal = async () => {
    try {
      const res = await axios.get(`${API}/proposals/sign/${token}`);
      setProposal(res.data);
      if (res.data.status === "accepted" || res.data.status === "rejected") setResponded(true);
    } catch {
      setError("Proposta não encontrada ou link inválido.");
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!accepted) { toast.error("Deve confirmar que leu e aceita a proposta"); return; }
    setSigning(true);
    try {
      await axios.post(`${API}/proposals/sign/${token}`, { accepted: true });
      setProposal(prev => ({ ...prev, status: "accepted", signed_at: new Date().toISOString() }));
      setResponded(true);
      toast.success("Proposta aceite com sucesso!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao aceitar proposta");
    } finally { setSigning(false); }
  };

  const handleReject = async () => {
    if (!rejected) { toast.error("Deve selecionar a opção de não aceitar"); return; }
    if (!rejectionReason || rejectionReason.trim().length < 10) {
      toast.error("Indique uma razão (mínimo 10 carateres) para a não aceitação");
      return;
    }
    setSigning(true);
    try {
      await axios.post(`${API}/proposals/sign/${token}`, { accepted: false, rejection_reason: rejectionReason.trim() });
      setProposal(prev => ({ ...prev, status: "rejected", signed_at: new Date().toISOString(), rejection_reason: rejectionReason.trim() }));
      setResponded(true);
      toast.success("Resposta registada.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao registar resposta");
    } finally { setSigning(false); }
  };

  const handleDownloadPdf = async () => {
    if (!proposal?.id) { toast.error("Proposta inválida"); return; }
    try {
      const res = await axios.post(`${API}/proposals/${proposal.id}/generate-signed-pdf`, {}, { responseType: "blob" });
      if (!res.data || res.data.size === 0) { toast.error("PDF vazio"); return; }
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Proposta_${proposal.candidate_name?.split(" ")[0] || "proposta"}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("PDF gerado!");
    } catch (e) {
      const msg = e?.response?.status ? `Erro ${e.response.status}: ${e.response.statusText || 'ao gerar PDF'}` : (e?.message || "Erro ao gerar PDF");
      toast.error(msg);
      console.error("PDF error:", e);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Card className="max-w-md"><CardContent className="p-8 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold mb-2">Erro</h2>
        <p className="text-muted-foreground">{error}</p>
      </CardContent></Card>
    </div>
  );

  const result = proposal?.results?.results?.[0];
  const simData = proposal?.simulation_data || {};
  const isContract = simData.regime_type === "contract" || !simData.regime_type;
  const gross = result?.gross_monthly || 0;
  const netMonthly = result?.net_monthly || 0;
  const irs = result?.irs_retention || 0;
  const meal = result?.meal_allowance || 0;
  const km = result?.km_allowance || 0;
  const ppr = result?.insurance_capitalization || 0;
  const ticket = result?.ticket_infancia || 0;
  const ss = result?.social_security_employee || 0;
  const mealMonthsCfg = parseInt(simData?.meal_allowance_months) || 11;
  const kmMonthsCfg = parseInt(simData?.km_allowance_months) || 11;
  // Ajudas de Custo — pagas em N meses (default 11), mostradas DISTRIBUÍDAS sobre 12
  const kmDistributed = (km * kmMonthsCfg) / 12;
  // Fonte de verdade = backend fields `liq_14_monthly` e `liq_12_monthly` (2026-02-25).
  // Para simulações antigas sem esses campos, deriva do `net_monthly` como fallback.
  const netMonthlyBackend = Number(result?.net_monthly) || 0;
  const netAnnualBackend = Number(result?.net_annual) || 0;
  const liq14Backend = Number(result?.liq_14_monthly);
  const liq12Backend = Number(result?.liq_12_monthly);
  const subsidyNet = gross - irs - ss;
  const liq12 = Number.isFinite(liq12Backend) && liq12Backend > 0 ? liq12Backend : netMonthlyBackend;
  const liq14 = Number.isFinite(liq14Backend) && liq14Backend > 0
    ? liq14Backend
    : (netMonthlyBackend - (subsidyNet * 2) / 12);
  // Total anual — mesma fonte de verdade que o PDF usa
  const backendNetAnnual = netAnnualBackend || (liq14 * 12 + subsidyNet * 2);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="proposal-sign-page">
      <header className="bg-[var(--brand-primary)] text-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}/api/static/evolutt_logo_white.png`}
            alt="Evolutt"
            className="h-8"
            data-testid="proposal-logo"
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/candidates")}
            className="text-white hover:bg-white/10"
            data-testid="proposal-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        {/* Status banners */}
        {proposal.status === "accepted" && (
          <div className="flex items-center gap-3 p-4 bg-brand-50 border-2 border-brand-300 rounded-xl" data-testid="signed-banner">
            <CheckCircle2 className="w-6 h-6 text-brand-600 shrink-0" />
            <div>
              <p className="font-semibold text-brand-800">Proposta Aceite</p>
              <p className="text-sm text-brand-600">Aceite em {proposal.signed_at ? new Date(proposal.signed_at).toLocaleString("pt-PT") : "—"}</p>
            </div>
            <Button variant="outline" size="sm" className="ml-auto" onClick={handleDownloadPdf} data-testid="download-signed-pdf-btn">
              <Download className="w-4 h-4 mr-2" />PDF
            </Button>
          </div>
        )}
        {proposal.status === "rejected" && (
          <div className="flex items-center gap-3 p-4 bg-red-50 border-2 border-red-300 rounded-xl" data-testid="rejected-banner">
            <XCircle className="w-6 h-6 text-red-600 shrink-0" />
            <div>
              <p className="font-semibold text-red-800">Proposta Não Aceite</p>
              <p className="text-sm text-red-600">{proposal.signed_at ? new Date(proposal.signed_at).toLocaleString("pt-PT") : ""}</p>
              {proposal.rejection_reason && <p className="text-sm text-red-500 mt-1">Razão: {proposal.rejection_reason}</p>}
            </div>
          </div>
        )}

        {/* Candidate info */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-[var(--brand-primary)]" />
              <h2 className="font-bold text-lg text-[var(--brand-primary)]">Proposta de Trabalho</h2>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><p className="text-muted-foreground">Candidato</p><p className="font-semibold">{proposal.candidate_name || "—"}</p></div>
              {proposal.candidate_function && <div><p className="text-muted-foreground">Função</p><p className="font-semibold">{proposal.candidate_function}</p></div>}
              <div><p className="text-muted-foreground">Data</p><p className="font-semibold">{new Date(proposal.created_at).toLocaleDateString("pt-PT")}</p></div>
              <div>
                <p className="text-muted-foreground">Estado</p>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  proposal.status === "accepted" ? "bg-brand-100 text-brand-800" :
                  proposal.status === "rejected" ? "bg-red-100 text-red-800" :
                  "bg-amber-100 text-amber-800"
                }`}>
                  {proposal.status === "accepted" ? "Aceite" : proposal.status === "rejected" ? "Não Aceite" : "Pendente"}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Financial details — Recibos Verdes / B2B */}
        {result && !isContract && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Euro className="w-5 h-5 text-[var(--brand-primary)]" />
                <h2 className="font-bold text-lg text-[var(--brand-primary)]">
                  Resumo Financeiro — {simData.regime_type === "b2b" ? "B2B" : "Recibos Verdes"}
                </h2>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between p-3 bg-slate-50 rounded-lg">
                  <span>Função</span>
                  <span className="font-semibold">{proposal.candidate_function || "—"}</span>
                </div>
                <div className="flex justify-between p-3 bg-slate-50 rounded-lg">
                  <span>Relação Contratual</span>
                  <span className="font-semibold">
                    {simData.regime_type === "b2b" ? "Prestação de Serviços (B2B)" : "Prestação de Serviços (Recibos Verdes)"}
                  </span>
                </div>
                <div className="flex justify-between p-3 bg-slate-50 rounded-lg">
                  <span>Meses de Faturação</span>
                  <span className="font-semibold">{parseInt(simData.freelancer_billing_months || 11)} meses</span>
                </div>
                {(() => {
                  const raw = parseFloat(simData.daily_rate || 0);
                  const vt = simData.value_type || "daily";
                  const hpd = parseInt(simData.hours_per_day || 8);
                  let label = "Valor / Dia";
                  let val = raw;
                  if (vt === "hourly") { label = "Valor / Dia"; val = raw * hpd; }
                  else if (vt === "monthly") { label = "Valor / Mês"; val = raw; }
                  return (
                    <div className="flex justify-between p-3 bg-brand-100 rounded-lg font-bold border-2 border-brand-300">
                      <span>{label}</span>
                      <span className="font-mono text-brand-700">{fmt(val)}</span>
                    </div>
                  );
                })()}
                {(() => {
                  const health = parseFloat(simData.health_insurance_cost || 0) || parseFloat(result.health_insurance || 0);
                  const isB2B = (simData.regime_type === "b2b");
                  const gross = parseFloat(result.gross_monthly || 0);
                  const liquido = parseFloat(result.net_monthly || 0);
                  const valorBrutoMensal = gross + (health > 0 ? health : 0);
                  return (
                    <>
                      {health > 0 && (
                        <div className="flex justify-between p-3 bg-slate-50 rounded-lg">
                          <span>Seguro de Saúde</span>
                          <span className="font-mono font-semibold">{fmt(health)}</span>
                        </div>
                      )}
                      <div className="flex justify-between p-3 bg-[#FADCBC] rounded-lg font-bold text-[var(--brand-primary)] border-2 border-[#F09678]">
                        <span>Valor Bruto Mensal</span>
                        <span className="font-mono">{fmt(valorBrutoMensal)}</span>
                      </div>
                      {/* Valor Líquido Mensal — só Recibos Verdes */}
                      {!isB2B && liquido > 0 && (
                        <div className="flex justify-between p-3 bg-[#FADCBC] rounded-lg font-bold text-[var(--brand-primary)] border-2 border-[#F09678]">
                          <span>Valor Líquido Mensal</span>
                          <span className="font-mono">{fmt(liquido)}</span>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Financial details */}
        {result && isContract && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Euro className="w-5 h-5 text-[var(--brand-primary)]" />
                <h2 className="font-bold text-lg text-[var(--brand-primary)]">Resumo Financeiro</h2>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between p-3 bg-slate-50 rounded-lg">
                  <span>Salário Bruto Mensal</span>
                  <span className="font-mono font-bold">{fmt(gross)}</span>
                </div>
                {(result.include_iht || simData.business_type === "outsourcing") && (
                  <>
                    <div className="flex justify-between p-3 bg-slate-50 rounded-lg pl-8">
                      <span className="text-muted-foreground">Salário Base (80%)</span>
                      <span className="font-mono">{fmt(result.iht_base || gross * 0.8)}</span>
                    </div>
                    <div className="flex justify-between p-3 bg-slate-50 rounded-lg pl-8">
                      <span className="text-muted-foreground">IHT - Isenção Horário (20%)</span>
                      <span className="font-mono">{fmt(result.iht_amount || gross * 0.2)}</span>
                    </div>
                  </>
                )}
                {result.meal_allowance > 0 && (
                  <div className="flex justify-between p-3 bg-blue-50 rounded-lg">
                    <span>Subsídio de Alimentação</span>
                    <span className="font-mono text-blue-700">+{fmt(result.meal_allowance)}</span>
                  </div>
                )}
                {/* Seguro de Saúde — não aparece aqui (só na nota final abaixo da tabela) */}
                {ppr > 0 && (
                  <div className="flex justify-between p-3 bg-blue-50 rounded-lg">
                    <span>PPR / Capitalização</span>
                    <span className="font-mono text-blue-700">+{fmt(ppr)}</span>
                  </div>
                )}
                {km > 0 && (
                  <div className="flex justify-between p-3 bg-blue-50 rounded-lg">
                    <span>Deslocação</span>
                    <span className="font-mono text-blue-700">+{fmt(kmDistributed)}</span>
                  </div>
                )}
                {ticket > 0 && (
                  <div className="flex justify-between p-3 bg-blue-50 rounded-lg">
                    <span>Ticket Infância</span>
                    <span className="font-mono text-blue-700">+{fmt(ticket)}</span>
                  </div>
                )}
                <div className="flex justify-between p-3 bg-red-50 rounded-lg">
                  <span>IRS ({result.gross_monthly ? ((result.irs_retention / result.gross_monthly) * 100).toFixed(1) : 0}%)</span>
                  <span className="font-mono text-red-600">-{fmt(result.irs_retention)}</span>
                </div>
                <div className="flex justify-between p-3 bg-red-50 rounded-lg">
                  <span>Segurança Social (11%)</span>
                  <span className="font-mono text-red-600">-{fmt(ss)}</span>
                </div>
                {/* Liquidez — mostra ambos (14 e 12) com nomenclatura "Líquido" */}
                <div className="flex justify-between p-3 bg-brand-50 rounded-lg font-bold">
                  <span>Salário Líquido Mensal (14)</span>
                  <span className="font-mono text-brand-700">{fmt(liq14)}</span>
                </div>
                <div className="flex justify-between p-3 bg-brand-100 rounded-lg font-bold border-2 border-brand-300">
                  <span>Salário Líquido Mensal (12)</span>
                  <span className="font-mono text-brand-700">{fmt(liq12)}</span>
                </div>
                <div className="flex justify-between p-3 bg-slate-50 rounded-lg">
                  <span>Salário Bruto Anual (14 meses)</span>
                  <span className="font-mono font-semibold">{fmt(gross * 14)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Annual View */}
        {result && isContract && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Calendar className="w-5 h-5 text-[var(--brand-primary)]" />
                <h2 className="font-bold text-lg text-[var(--brand-primary)]">Visão Anual</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-[var(--brand-primary)] text-white">
                      <th className="text-left p-2 rounded-tl-lg">Componente</th>
                      {['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'].map(m => (
                        <th key={m} className="text-center p-1.5 w-[6.5%]">{m}</th>
                      ))}
                      <th className="text-center p-2 rounded-tr-lg font-bold">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const irs = result.irs_retention || 0;
                      const meal = result.meal_allowance || 0;
                      const isIht = result.include_iht || simData.business_type === "outsourcing";
                      // Subsídio de Férias em Junho (idx 5) e Subsídio de Natal em Novembro (idx 10)
                      // Helpers — distribuem o componente nos 12 meses + duplicam em Jun/Nov
                      const SUB_MONTHS = [5, 10]; // Jun, Nov
                      const withSubsidies = (v) => Array.from({ length: 12 }, (_, i) => SUB_MONTHS.includes(i) ? v * 2 : v);
                      const flatMonths = (v, months) => Array.from({ length: 12 }, (_, i) => i < months ? v : 0);
                      const rows = [];
                      // Componentes sujeitos a subsídios (×14)
                      rows.push({ label: "Salário Bruto", values: withSubsidies(gross), total: gross * 14 });
                      if (isIht) {
                        rows.push({ label: "Base (80%)", values: withSubsidies(gross * 0.8), total: gross * 0.8 * 14, sub: true });
                        rows.push({ label: "IHT (20%)", values: withSubsidies(gross * 0.2), total: gross * 0.2 * 14, sub: true });
                      }
                      // Benefícios — não dobram em Jun/Nov
                      if (meal > 0) rows.push({ label: "Sub. Alimentação", values: Array.from({ length: 12 }, (_, i) => i === 7 ? 0 : meal), total: meal * 11 });
                      if (km > 0) {
                        const kmMonths = parseInt(simData.km_allowance_months || 12);
                        // Distribuir (km × kmMonths) por 12 meses (mostra valor em todos os meses)
                        const kmPerMonthVisual = (km * kmMonths) / 12;
                        rows.push({ label: "Deslocação", values: Array(12).fill(kmPerMonthVisual), total: km * kmMonths });
                      }
                      if (ppr > 0) {
                        const pprMonths = parseInt(simData.insurance_capitalization_months || 12);
                        rows.push({ label: "Real Vida", values: flatMonths(ppr, pprMonths), total: ppr * pprMonths });
                      }
                      if (ticket > 0) {
                        const tiMonths = parseInt(simData.ticket_infancia_months || 12);
                        rows.push({ label: "T. Infância", values: flatMonths(ticket, tiMonths), total: ticket * tiMonths });
                      }
                      // Retenções — também aplicam aos subsídios
                      rows.push({ label: "SS (11%)", values: withSubsidies(-ss), total: -ss * 14, neg: true });
                      const irsEffRate = gross > 0 ? ((irs / gross) * 100).toFixed(1) : 0;
                      rows.push({ label: `IRS (${irsEffRate}%)`, values: withSubsidies(-irs), total: -irs * 14, neg: true });
                      // Líquido (14): liq14 todos os meses + 1 subsídio líquido em Jun e Nov.
                      // Em Agosto (idx 7) subtrai o Sub. Alimentação pois esse mês não recebe.
                      // Ajudas de Custo já vem distribuído (× 11/12) → não subtrai em Agosto.
                      // Total = backendNetAnnual (fonte única de verdade = igual à do PDF).
                      const liq14Vals = Array.from({ length: 12 }, (_, i) => {
                        let v = liq14;
                        if (SUB_MONTHS.includes(i)) v += subsidyNet;
                        if (i === 7) v -= meal; // Sub. Alimentação não pago em Agosto
                        return v;
                      });
                      rows.push({ label: "Salário Líquido Mensal (14)", values: liq14Vals, total: backendNetAnnual, highlight: true });
                      // Líquido (12): mesmo valor todos os meses (duodécimos diluem os subsídios)
                      // Total forçado a backendNetAnnual — garante consistência com (14) e com o PDF.
                      rows.push({ label: "Salário Líquido Mensal (12)", values: Array(12).fill(liq12), total: backendNetAnnual, highlight: true });
                      return rows.map((row, ri) => (
                        <tr key={ri} className={row.highlight ? "bg-[#FADCBC]/30 font-bold" : ri % 2 === 0 ? "bg-slate-50" : ""}>
                          <td className={`p-1.5 ${row.sub ? 'pl-4 text-muted-foreground' : ''} ${row.neg ? 'text-red-600' : ''}`}>{row.label}</td>
                          {row.values.map((v, i) => (
                            <td key={i} className={`text-center p-1 font-mono ${row.neg ? 'text-red-600' : ''}`}>
                              {v === 0 ? "—" : fmt(Math.abs(v))}
                            </td>
                          ))}
                          <td className={`text-center p-1.5 font-mono font-bold ${row.neg ? 'text-red-600' : ''}`}>{fmt(row.total)}</td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>
              {result.health_insurance > 0 && (
                <p className="text-xs text-[#466464] italic mt-3">
                  Seguro {proposal?.proposal_settings?.texts?.health_insurance_name || 'Saúde'} incluído na proposta.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* PDF Generation */}
        {proposal.status === "pending" && (
          <div className="flex justify-center">
            <Button variant="outline" onClick={handleDownloadPdf} className="border-[var(--brand-primary)] text-[var(--brand-primary)]" data-testid="download-pdf-btn">
              <Download className="w-4 h-4 mr-2" /> Gerar Proposta PDF
            </Button>
          </div>
        )}

        {/* Signing section - only when pending */}
        {!responded && proposal.status === "pending" && (
          <Card className="border-2 border-[var(--brand-primary)]" data-testid="signing-section">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-5 h-5 text-[var(--brand-primary)]" />
                <h2 className="font-bold text-lg text-[var(--brand-primary)]">Resposta à Proposta</h2>
              </div>

              {/* Accept option */}
              <div className={`flex items-start gap-3 p-4 rounded-lg mb-4 border-2 cursor-pointer transition-all ${accepted ? 'bg-brand-50 border-brand-400' : 'bg-slate-50 border-slate-200 hover:border-brand-300'}`}
                onClick={() => { setAccepted(true); setRejected(false); }}>
                <Checkbox checked={accepted} onCheckedChange={(v) => { setAccepted(v); if(v) setRejected(false); }} className="mt-0.5" data-testid="accept-checkbox" />
                <label className="text-sm leading-relaxed cursor-pointer">
                  <span className="font-semibold text-brand-700">Aceitar Proposta</span><br/>
                  Li e compreendi os termos desta proposta financeira. Confirmo que aceito as condições apresentadas.
                </label>
              </div>

              {/* Reject option */}
              <div className={`p-4 rounded-lg mb-4 border-2 cursor-pointer transition-all ${rejected ? 'bg-red-50 border-red-400' : 'bg-slate-50 border-slate-200 hover:border-red-300'}`}
                onClick={() => { setRejected(true); setAccepted(false); }}>
                <div className="flex items-start gap-3">
                  <Checkbox checked={rejected} onCheckedChange={(v) => { setRejected(v); if(v) setAccepted(false); }} className="mt-0.5" data-testid="reject-checkbox" />
                  <label className="text-sm leading-relaxed cursor-pointer">
                    <span className="font-semibold text-red-700">Não Aceitar Proposta</span><br/>
                    Não aceito as condições apresentadas nesta proposta.
                  </label>
                </div>
                {rejected && (
                  <div className="mt-3 ml-7">
                    <p className="text-xs text-muted-foreground mb-1">
                      Razão <span className="text-red-600">*obrigatória (mínimo 10 carateres)</span>:
                    </p>
                    <Textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="Indique as razões ou condições pretendidas..."
                      className={`min-h-[80px] text-sm ${rejectionReason.trim().length > 0 && rejectionReason.trim().length < 10 ? 'border-red-400' : ''}`}
                      data-testid="rejection-reason"
                    />
                    <p className={`text-[10px] mt-1 ${rejectionReason.trim().length >= 10 ? 'text-brand-600' : 'text-muted-foreground'}`}>
                      {rejectionReason.trim().length}/10 carateres mínimo
                    </p>
                  </div>
                )}
              </div>

              {/* Submit buttons */}
              <div className="flex gap-3">
                {accepted && (
                  <Button onClick={handleAccept} disabled={signing} className="flex-1 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary)] text-white h-12 text-base font-bold" data-testid="sign-btn">
                    {signing ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <CheckCircle2 className="w-5 h-5 mr-2" />}
                    Aceitar Proposta
                  </Button>
                )}
                {rejected && (
                  <Button onClick={handleReject} disabled={signing || rejectionReason.trim().length < 10} variant="destructive" className="flex-1 h-12 text-base font-bold" data-testid="reject-btn">
                    {signing ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <XCircle className="w-5 h-5 mr-2" />}
                    Não Aceitar
                  </Button>
                )}
              </div>

              <p className="text-xs text-muted-foreground text-center mt-3">A data e hora serão registadas como comprovativo.</p>
            </CardContent>
          </Card>
        )}

        <div className="text-center text-xs text-slate-400 py-4">
          <p>© {new Date().getFullYear()} Evolutt - Unlocking What&apos;s Next</p>
        </div>
      </main>
    </div>
  );
};

export default ProposalSignPage;
